import * as Sentry from '@sentry/browser';

/**
 * RSP-6971: give Sentry a real, titled exception for captured GraphQL / network
 * errors.
 *
 * Problem: the Apollo onError bridge captures raw values — plain `graphQLErrors`
 * objects (NOT real `GraphQLError` instances), bare message strings, and
 * non-Error `networkError` objects. `Sentry.captureException` can't derive a
 * `type: value` from those, so the issue title renders as `<unknown>`.
 *
 * Fix: normalise into a typed Error whose name + message produce a readable
 * title (`GraphQLOperationError: <operation> — <message>`), and fingerprint by
 * operation + message so grouping is stable and per-operation.
 */

export type GraphqlErrorKind = 'graphql' | 'network';

/**
 * Operations whose captured errors get the titled + per-operation-fingerprinted
 * treatment. This is an explicit opt-in allowlist, NOT a blanket rule: the
 * titled path changes issue fingerprints, so opting an operation in reshapes its
 * Sentry grouping. Keep it a small, reviewed list — a broad pattern would
 * regroup unrelated teams' issues.
 *
 * To opt a feature in, add a pattern here — a data change in this module, not
 * per-feature branching in the Apollo bridge.
 */
const TITLED_OPERATION_PATTERNS: RegExp[] = [/oversight/i];

/** Whether a captured error for this operation should use the titled path. */
export const shouldTitleOperation = (operationName: string): boolean =>
  TITLED_OPERATION_PATTERNS.some((pattern) => pattern.test(operationName));

/** Typed error so Sentry titles as `GraphQLOperationError: …`. */
export class GraphQLOperationError extends Error {
  override name = 'GraphQLOperationError';
  constructor(
    message: string,
    readonly operationName: string,
    readonly kind: GraphqlErrorKind,
    options?: { cause?: unknown }
  ) {
    super(message, options);
  }
}

// Bound what reaches the Sentry title (RSP-6971 review): a raw message can echo
// user input / record IDs / a full server body, and the title propagates to
// issue lists, alert emails and Slack. Cap length; the untruncated original
// still lives in `extra.rawError` (subject to Sentry data-scrubbing) for debug.
const MAX_MESSAGE_LEN = 200;
const truncate = (value: string): string =>
  value.length > MAX_MESSAGE_LEN
    ? `${value.slice(0, MAX_MESSAGE_LEN)}…`
    : value;

/** Best-effort human message out of the many shapes Apollo hands us. */
export const extractGraphqlErrorMessage = (raw: unknown): string => {
  if (typeof raw === 'string') {
    return truncate(raw);
  }
  if (raw instanceof Error && raw.message) {
    return truncate(raw.message);
  }
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (typeof obj.message === 'string' && obj.message) {
      return truncate(obj.message);
    }
    // Apollo networkError with a server body sometimes nests the real message.
    const result = obj.result as Record<string, unknown> | undefined;
    if (result && typeof result.message === 'string' && result.message) {
      return truncate(result.message);
    }

    // No usable message: don't spill the whole serialised body into the title —
    // emit a bounded, generic descriptor. Full object stays in extra.rawError.
    return `<unserialised ${obj.constructor?.name ?? 'error'}>`;
  }

  return truncate(String(raw));
};

/**
 * Capture a GraphQL/network error with a real title + per-operation grouping.
 * Drop-in for the raw `handleError(rawError, …)` calls in the Apollo bridge.
 */
export const captureGraphqlOperationError = (
  rawError: unknown,
  args: {
    operationName: string;
    kind: GraphqlErrorKind;
    extra?: Record<string, unknown>;
    tags?: Record<string, string>;
  }
): void => {
  const { operationName, kind, extra, tags } = args;
  const message = extractGraphqlErrorMessage(rawError);

  const titled = new GraphQLOperationError(
    `${operationName} — ${message}`,
    operationName,
    kind,
    { cause: rawError }
  );

  // Parity with `handleError` (errorUtils.ts): set the transaction name so
  // events keep populating Sentry's Transaction field (transaction:<op> saved
  // searches / alerts). Dropped accidentally when this path bypassed
  // handleError — RSP-6971 review S2.
  Sentry.getCurrentScope().setTransactionName(operationName);

  Sentry.captureException(titled, {
    // Group per OPERATION only (the bridge stack is identical for every op).
    // Deliberately excludes the message: a message that embeds record IDs /
    // counts would fragment one logical problem into an issue per value and
    // persist that value in the grouping key — RSP-6971 review S3.
    fingerprint: ['graphql', kind, operationName],
    extra: { operationName, rawError, ...extra },
    // Canonical `graphql_operation` set LAST so a caller-supplied tag can never
    // override the operation-name contract that alerts/dashboards rely on.
    tags: { ...tags, graphql_operation: operationName },
  });

  console.error(`[${kind}] ${operationName}:`, rawError);
};
